import Remarkable from 'remarkable';
import RemarkableReactRenderer from 'remarkable-react';

import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';

import styles from './Markdown.module.scss';

/**
 * @param {string} markdown
 * @returns {{__html: string | *}}
 */
export default function Markdown(markdown, options) {
  if (typeof options === 'undefined') {
    options = {
      // If we don't want to allow zero, or top-level, paragraphs (like in the case of request
      // parameter markdown descriptions), then we should process this as a normal span element
      // if: it is a single element, and it is a string (and not a single element-object).
      allowLevelZeroParagraphs: true,

      // There are cases where we won't want any Markdown paragraphs to be converted into
      // paragraphs, but rather span tags.
      convertParagraphsToSpans: false,

      // There are cases where we don't want **any** nested paragraphs. So like with the following
      // content:
      //
      //  The contextual action:
      //    - `Added to` - An Added To action.
      //
      // We don't want the "Added to" bit to be nested into another paragrpah because it'll be
      // formatted improperly. Instead that should be wrapped within a `<span>` tag.
      allowNestedParagraphs: true,

      // Whether or not you want lists to remain unstyled and "bare".
      unstyledLists: false,
    };
  }

  // Decode `<script />` tags that might be present in the markdown. We're doing this because we
  // had to slightly encode `<script />` tags before sending them down into the browser window
  // because having them causes the `laracasts/utilities` package to choke.
  markdown = markdown.replace(/&lt;script/g, '<script');
  markdown = markdown.replace(/&gt;\/script/g, '</script');

  const md = new Remarkable();

  // We're rewriting most of these elements to just pass the childre down so we don't pass unknown
  // props to Iris like `lines`, `level`, and `options‘. Fixes the following React error:
  //
  //    Warning: Unknown props `lines`, `level`, `options` on <ul> tag. Remove these props
  //    from the element. For details, see https://fb.me/react-unknown-prop
  //
  md.renderer = new RemarkableReactRenderer({
    components: {
      a: (props) => (
        <Link variant="minimal" href={props.href}>
          {props.children}
        </Link>
      ),
      code: function (props) {
        // If there's no `params` prop, then this is just a standard `code` Markdown element, and
        // not a full code example.
        if (typeof props.params === 'undefined') {
          return <code>{props.children}</code>;
        }

        return <CodeBlock language={props.params}>{props.children}</CodeBlock>;
      },
      h1: (props) => <Header>{props.children}</Header>,
      h2: (props) => <Header size="2">{props.children}</Header>,
      h3: (props) => <Header size="3">{props.children}</Header>,
      h4: (props) => <Header size="4">{props.children}</Header>,
      h5: (props) => <Header size="5">{props.children}</Header>,
      h6: (props) => <Header size="6">{props.children}</Header>,
      li: (props) => <li>{props.children}</li>,
      p: function (props) {
        if (options.convertParagraphsToSpans) {
          return <span>{props.children}</span>;
        }

        if (
          !options.allowLevelZeroParagraphs &&
          props.level === 0 &&
          props.children.length === 1 &&
          typeof props.children[0] === 'string'
        ) {
          return <span>{props.children}</span>;
        }

        if (props.level > 0 && !options.allowNestedParagraphs) {
          return <span>{props.children}</span>;
        }

        return <Paragraph size="2">{props.children}</Paragraph>;
      },
      // Delete `<pre>` tags from Markdown since they're always going to be wrapping `<code>`
      // and we have special handling for those.
      pre: (props) => <div>{props.children}</div>,
      ul: function (props) {
        if (options.unstyledLists) {
          return (
            <ul format="bare" className={styles.bareList}>
              {props.children}
            </ul>
          );
        }

        return <ul>{props.children}</ul>;
      },
    },
  });

  return md.render(markdown);
}
